import { useQuery, useReactiveVar } from '@apollo/client';
import { ChoiceGroup, Stack } from '@fluentui/react';
import {
  DropdownFilter,
  FilterArrayType,
  FilterElement,
} from 'common/components/Filters';
import { ToggleFilter } from 'common/components/Filters/ToggleFilter';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import { choiceGroupStyles, options } from 'common/utils/listViewOptionStyle';
import { setPOListSupplierViewOptions } from 'purchaseOrder/view/FormView/SupplierModal/SupplierModalPOListView';
import React, { useState } from 'react';
import { globalMode } from 'utility/cache/ui';
import { setPOListViewOptions } from '..';
import { PurchaseOrderFiltersData } from './__generated__/PurchaseOrderFiltersData';
import { useStyles } from './index.styles';
import PURCHASE_ORDER_FILTERS_DATA from './PurchaseOrderFiltersData.graphql';

interface POSearchBarProps extends SearchBarProps {
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
  isSigning: boolean;
  isPOSupplierList?: boolean;
}

export const POSearchBar: React.FC<POSearchBarProps> = ({
  columns,
  onFilterChange,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  urlParams,
  onGlobalToggle,
  isSigning,
  isPOSupplierList = false,
}) => {
  const globalState = useReactiveVar(globalMode);
  const styles = useStyles();
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const POListViewOptions = useReactiveVar(setPOListViewOptions);
  const POListSupplierViewOptions = useReactiveVar(
    setPOListSupplierViewOptions
  );

  const { data: purchaseOrderFiltersData } = useQuery<PurchaseOrderFiltersData>(
    PURCHASE_ORDER_FILTERS_DATA
  );

  const supplierTypeOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.availableSuppliers?.nodes,
    {
      getKey: (item) => item.id || '',
      getText: (item) => item._fullName || '',
      includeAll: true,
    }
  );
  const purchaseOrderTypeOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyPurchaseOrderTypes?.nodes,
    {
      getKey: (item) => item.purchaseOrderTypeId || '',
      getText: (item) => item.purchaseOrderType || '',
      includeAll: true,
    }
  );

  const departmentOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyDepartments?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const businessUnitOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyBusinessUnits?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const currencyOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: true,
    }
  );

  const statusOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType || '',
    }
  );
  const createdByOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderUsers?.nodes,
    {
      getKey: (item) => item.userId || '',
      getText: (item) => item.userName || '',
      includeAll: true,
    }
  );
  const accountHistoryOptions = formatDropdownOptions(
    purchaseOrderFiltersData?.purchaseOrderAccountingHistories?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.exportInfo ?? '',
      includeAll: true,
      multiSelect: true,
    }
  );
  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  const filters: FilterElement[] = [];
  if (!isPOSupplierList) {
    filters.push(
      <DropdownFilter
        filterKey="filterSupplierId"
        options={supplierTypeOptions || []}
        placeholder="Supplier"
        onOptionChange={onOptionChange}
      />
    );
  }

  filters.push(
    ...[
      <DropdownFilter
        filterKey="purchaseOrderTypeId"
        options={purchaseOrderTypeOptions || []}
        placeholder="Type"
        onOptionChange={onOptionChange}
      />,
      <DropdownFilter
        filterKey="departmentId"
        options={departmentOptions || []}
        placeholder="Department"
        onOptionChange={onOptionChange}
      />,
      <DropdownFilter
        filterKey="businessUnitId"
        options={businessUnitOptions || []}
        placeholder="Business Unit"
        onOptionChange={onOptionChange}
      />,
      <DropdownFilter
        filterKey="currencyId"
        options={currencyOptions || []}
        placeholder="Currency"
        onOptionChange={onOptionChange}
      />,
      <DropdownFilter
        filterKey="filterMultiStatusTypes"
        options={statusOptions}
        placeholder="Status"
        onOptionChange={onOptionChange}
        multiSelect
        maxWidth={150}
      />,
      <DropdownFilter
        filterKey="_createdByUserid"
        options={createdByOptions}
        placeholder="Created By"
        onOptionChange={onOptionChange}
      />,
      <DropdownFilter
        filterKey="_accountingExportCycleId"
        options={accountHistoryOptions}
        placeholder="Exported"
        disabled={isSigning}
        onOptionChange={onOptionChange}
        width={150}
        maxHeight={400}
      />,
      <ToggleFilter
        placeholder="New"
        disabled={isSigning}
        inlineLabel
        filterKey="isFilterUnprocessedDocuments"
        onOptionChange={onOptionChange}
      />,
    ]
  );

  return (
    <SearchBar
      urlParams={urlParams}
      onFilterClear={() => {
        if (filterTypes.length) onFilterChange?.([]);
        setFilterTypes([]);
      }}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      onGlobalToggle={onGlobalToggle}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      filters={filters}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontalAlign="center"
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <ChoiceGroup
            options={options}
            disabled={globalState}
            selectedKey={
              isPOSupplierList
                ? POListSupplierViewOptions.isHistory
                  ? 'History'
                  : POListSupplierViewOptions.isSigning
                    ? 'Signing'
                    : 'Accounting'
                : POListViewOptions.isHistory
                  ? 'History'
                  : POListViewOptions.isSigning
                    ? 'Signing'
                    : 'Accounting'
            }
            styles={choiceGroupStyles}
            onChange={(ev, option) => {
              if (!option) return;

              const isSigning = option.key === 'Signing';
              const isHistory = option.key === 'History';

              if (!isPOSupplierList) {
                setPOListViewOptions({
                  ...POListViewOptions,
                  isSigning,
                  isHistory,
                });
              } else {
                setPOListSupplierViewOptions({
                  ...POListSupplierViewOptions,
                  isSigning,
                  isHistory,
                });
              }
            }}
          />
        </Stack>
      }
    />
  );
};
